import {
  forwardRef,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";

import { Badge } from "./Badge";
import { Button } from "./Button";
import { CommandPalette, type CommandPaletteProps } from "./CommandPalette";
import { InlineError } from "./Feedback";
import { Inline, Stack } from "./Layout";
import { useAnchoredLayerDismiss } from "./layerUtils";
import { Select, TextInput } from "./TextInput";
import { Text } from "./Typography";

export type ShellIconName =
  | "alerts"
  | "apiKeys"
  | "arrowLeft"
  | "arrowUpRight"
  | "bot"
  | "book"
  | "building"
  | "check"
  | "chevronDown"
  | "clipboardCheck"
  | "copy"
  | "dashboard"
  | "ellipsis"
  | "ellipsisVertical"
  | "externalLink"
  | "fileText"
  | "flask"
  | "help"
  | "listFilter"
  | "logOut"
  | "mail"
  | "monitor"
  | "moon"
  | "panel"
  | "pencil"
  | "play"
  | "plus"
  | "route"
  | "search"
  | "settings"
  | "sun"
  | "trash"
  | "trend"
  | "user"
  | "users";

function classes(base: string[], className: string | undefined): string {
  if (className) {
    base.push(className);
  }

  return base.join(" ");
}

export interface ShellBreadcrumb {
  label: string;
  onClick?: () => void;
}

export interface ScopeSummary {
  organizationInitials: string;
  organizationName: string;
}

export interface AppShellProps {
  children: ReactNode;
  commandPalette?: Omit<CommandPaletteProps, "onClose" | "searchIcon">;
  commandPaletteOpen?: boolean;
  isSidebarCollapsed?: boolean;
  onCloseCommandPalette?: () => void;
  sidebar: ReactNode;
  topBar: ReactNode;
}

export function AppShell({
  children,
  commandPalette,
  commandPaletteOpen = false,
  isSidebarCollapsed = false,
  onCloseCommandPalette,
  sidebar,
  topBar,
}: AppShellProps) {
  const commandPaletteProps = commandPalette ?? {
    agentItems: [],
    hasNextAgentPage: false,
    isAgentsError: false,
    isAgentsPending: false,
    navigateItems: [],
  };

  return (
    <div
      className={classes(
        ["marker-app-shell"],
        isSidebarCollapsed ? "marker-app-shell--collapsed" : undefined,
      )}
    >
      {sidebar}
      {topBar}
      <main className="marker-app-shell__main">
        {/* __main clips the workspace's inset corner (overflow: hidden);
            __content is the real scroll container. */}
        <div className="marker-app-shell__content">{children}</div>
      </main>
      {commandPaletteOpen ? (
        <CommandPalette
          {...commandPaletteProps}
          onClose={onCloseCommandPalette ?? noop}
          searchIcon={<ShellIcon name="search" />}
        />
      ) : null}
    </div>
  );
}

function noop() {
  return undefined;
}

export interface PrimarySidebarProps {
  // The sidebar agent block (UI-LAW §8): the single agent selector, rendered
  // between the scope header row and the nav rail.
  agentBlock?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  isCollapsed?: boolean;
  onToggleCollapsed: () => void;
  // The team selector occupying the scope header row (UI-LAW §8). No logo or
  // wordmark renders in the chrome — brand lives in the favicon and the auth
  // screens.
  teamSelector?: ReactNode;
}

export function PrimarySidebar({
  agentBlock,
  children,
  footer,
  isCollapsed = false,
  onToggleCollapsed,
  teamSelector,
}: PrimarySidebarProps) {
  return (
    <aside className="marker-primary-sidebar">
      <div className="marker-primary-sidebar__scope">
        {teamSelector}
        <button
          aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
          className="marker-primary-sidebar__collapse"
          onClick={onToggleCollapsed}
          title={isCollapsed ? "Expand navigation" : "Collapse navigation"}
          type="button"
        >
          <ShellIcon name="panel" />
        </button>
      </div>
      {agentBlock}
      <nav aria-label="Primary" className="marker-primary-sidebar__nav">
        {children}
      </nav>
      <div className="marker-primary-sidebar__spacer" />
      {footer ? (
        <div className="marker-primary-sidebar__footer">{footer}</div>
      ) : null}
    </aside>
  );
}

export interface TeamSelectorItem {
  id: string;
  label: string;
}

export interface TeamSelectorProps {
  // The resolved working team (docs/09 §4). The selector always renders —
  // a caller with exactly one team still sees it; it names what the rail
  // below shows.
  activeTeamId?: string;
  activeTeamLabel?: string;
  canCreateTeam?: boolean;
  hasNextPage?: boolean;
  isError?: boolean;
  isOpen: boolean;
  isPending?: boolean;
  onCreateTeam?: () => void;
  onLoadMore?: () => void;
  onOpenChange: (open: boolean) => void;
  onSelectTeam: (teamId: string) => void;
  teams: readonly TeamSelectorItem[];
}

// The team selector — the topmost chrome element (UI-LAW §8): it holds the
// working team and lists the caller's membership teams (`GET /v1/teams`,
// already membership-filtered). Team selection among the caller's own teams
// is live; organization switching does not exist in-product.
export function TeamSelector({
  activeTeamId,
  activeTeamLabel,
  canCreateTeam = false,
  hasNextPage = false,
  isError = false,
  isOpen,
  isPending = false,
  onCreateTeam,
  onLoadMore,
  onOpenChange,
  onSelectTeam,
  teams,
}: TeamSelectorProps) {
  const label = activeTeamLabel ?? "Team";
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // The working team leads its own menu so the current choice is readable
  // without scanning the list for the check.
  const orderedTeams = useMemo(
    () => [
      ...teams.filter((team) => team.id === activeTeamId),
      ...teams.filter((team) => team.id !== activeTeamId),
    ],
    [activeTeamId, teams],
  );

  useAnchoredLayerDismiss({
    active: isOpen,
    anchorRef: triggerRef,
    layerRef: menuRef,
    onDismiss: () => {
      onOpenChange(false);
    },
  });

  return (
    <div className="marker-team-selector">
      <button
        aria-expanded={isOpen}
        aria-label={`Team: ${label}`}
        className="marker-team-selector__trigger"
        onClick={() => {
          onOpenChange(!isOpen);
        }}
        ref={triggerRef}
        title={label}
        type="button"
      >
        {/* Collapsed rail shows this team icon in place of the name +
            chevron (CSS toggles visibility off the collapsed shell class). */}
        <span className="marker-team-selector__icon">
          <ShellIcon name="users" />
        </span>
        <span className="marker-team-selector__name marker-fade-truncate">
          {label}
        </span>
        <ShellIcon name="chevronDown" />
      </button>
      {isOpen ? (
        <div className="marker-team-selector__menu" ref={menuRef}>
          {/* No current-team header row: the trigger above already names the
              working team, and UI-LAW §8 specifies this menu as team rows
              with an active check. The create action leads instead, so the
              list below is purely the choices. */}
          {canCreateTeam && onCreateTeam ? (
            <button
              className="marker-menu-row marker-menu-row--lead"
              onClick={() => {
                onOpenChange(false);
                onCreateTeam();
              }}
              type="button"
            >
              <ShellIcon name="plus" />
              <span>Create team</span>
            </button>
          ) : null}
          {isPending ? (
            <div className="marker-team-selector__note">Loading teams…</div>
          ) : null}
          {isError ? (
            <div className="marker-team-selector__note">
              Could not load teams.
            </div>
          ) : null}
          {orderedTeams.map((team) => {
            const isActive = team.id === activeTeamId;
            return (
              <button
                aria-current={isActive ? "true" : undefined}
                className={classes(
                  [
                    "marker-menu-row",
                    isActive ? "marker-menu-row--active" : "",
                  ],
                  undefined,
                )}
                key={team.id}
                onClick={() => {
                  onOpenChange(false);
                  onSelectTeam(team.id);
                }}
                title={team.label}
                type="button"
              >
                <span className="marker-fade-truncate">{team.label}</span>
                {isActive ? <ShellIcon name="check" /> : null}
              </button>
            );
          })}
          {hasNextPage && onLoadMore ? (
            <button
              className="marker-menu-row"
              onClick={onLoadMore}
              type="button"
            >
              <ShellIcon name="chevronDown" />
              <span>Load more teams</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export interface SidebarAgentSectionProps {
  children: ReactNode;
}

// The agent selector wrapper (UI-LAW §8): the single agent scope selector,
// labeled like the team/project scope above it rather than duplicated in rail
// navigation.
export function SidebarAgentSection({ children }: SidebarAgentSectionProps) {
  return (
    <section className="marker-sidebar-agent">
      <div className="marker-sidebar-agent__title">Agent</div>
      {children}
    </section>
  );
}

export interface SidebarSectionProps {
  children: ReactNode;
  title?: string;
}

export function SidebarSection({ children, title }: SidebarSectionProps) {
  return (
    <section className="marker-sidebar-section">
      {title ? (
        <div className="marker-sidebar-section__title">{title}</div>
      ) : null}
      <div className="marker-sidebar-section__items">{children}</div>
    </section>
  );
}

export interface SidebarItemProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  icon: ShellIconName;
  isActive?: boolean;
  isSoon?: boolean;
  label: string;
}

export const SidebarItem = forwardRef<HTMLButtonElement, SidebarItemProps>(
  function SidebarItem(
    {
      className,
      icon,
      isActive = false,
      isSoon = false,
      label,
      onClick,
      ...props
    },
    ref,
  ) {
    return (
      <button
        aria-current={isActive ? "page" : undefined}
        aria-disabled={isSoon || undefined}
        className={classes(
          [
            "marker-sidebar-item",
            isActive ? "marker-sidebar-item--active" : "",
            isSoon ? "marker-sidebar-item--soon" : "",
          ],
          className,
        )}
        onClick={isSoon ? undefined : onClick}
        ref={ref}
        tabIndex={isSoon ? -1 : props.tabIndex}
        title={isSoon ? `${label} · Soon` : label}
        type="button"
        {...props}
      >
        <ShellIcon name={icon} />
        <span className="marker-sidebar-item__label">{label}</span>
        {isSoon ? (
          <Badge className="marker-sidebar-item__soon">Soon</Badge>
        ) : null}
      </button>
    );
  },
);

export interface OrganizationMenuItem {
  icon: ShellIconName;
  isActive?: boolean;
  label: string;
  onSelect: () => void;
}

export interface OrganizationMenuProps extends ScopeSummary {
  menuItems?: readonly OrganizationMenuItem[];
  onOrganizationSettings?: () => void;
}

// The bottom-left organization menu (docs/09 §4): the one session org's
// identity row plus a single Settings entry (→ /settings/organization; the
// full settings row list lives in the settings shell). Org creation,
// deletion, and switching do not exist in-product.
// OPEN(multi-org-membership): keep this a single identity row. A switcher, a
// cross-tenant org list, or a "current org" marker implying alternatives all
// wait on docs/open-questions.md#multi-org-membership.
export function OrganizationMenu({
  menuItems,
  organizationInitials,
  organizationName,
  onOrganizationSettings,
}: OrganizationMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const resolvedMenuItems =
    menuItems ??
    (onOrganizationSettings
      ? [
          {
            icon: "settings" as const,
            label: "Settings",
            onSelect: onOrganizationSettings,
          },
        ]
      : []);

  useAnchoredLayerDismiss({
    active: isOpen,
    anchorRef: triggerRef,
    layerRef: menuRef,
    onDismiss: () => {
      setIsOpen(false);
    },
  });

  return (
    <div className="marker-organization-menu">
      <button
        aria-label={organizationName}
        aria-expanded={isOpen}
        className="marker-organization-menu__trigger"
        onClick={() => {
          setIsOpen((current) => !current);
        }}
        ref={triggerRef}
        title={organizationName}
        type="button"
      >
        <span className="marker-organization-menu__tile">
          {organizationInitials}
        </span>
        <span className="marker-organization-menu__name">
          {organizationName}
        </span>
        <ShellIcon name="chevronDown" />
      </button>
      {isOpen ? (
        <div className="marker-organization-menu__popover" ref={menuRef}>
          {/* No "Current org" meta: the session resolves exactly one
              organization (`list_organizations` filters by
              `principal.organization_id`), so marking the only row as the
              current one is redundant — and it competed with the org name for
              this row's width, truncating to "Current or…". A current-vs-other
              marker belongs in a menu that lists alternatives, as the team
              selector's `marker-menu-row--active` does. */}
          <div className="marker-menu-current">
            <span className="marker-menu-current__label">
              {organizationName}
            </span>
          </div>
          {resolvedMenuItems.map((item) => (
            <button
              aria-current={item.isActive ? "page" : undefined}
              className={classes(
                [
                  "marker-menu-row",
                  item.isActive ? "marker-menu-row--active" : "",
                ],
                undefined,
              )}
              key={item.label}
              onClick={() => {
                setIsOpen(false);
                item.onSelect();
              }}
              type="button"
            >
              <ShellIcon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export interface BreadcrumbsProps {
  items: ShellBreadcrumb[];
}

export function Breadcrumbs({ items }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb" className="marker-breadcrumbs">
      <ol>
        {items.map((item, index) => {
          const isCurrent = index === items.length - 1;
          return (
            <li key={`${item.label}-${String(index)}`}>
              {isCurrent || !item.onClick ? (
                <span aria-current={isCurrent ? "page" : undefined}>
                  {item.label}
                </span>
              ) : (
                <button onClick={item.onClick} type="button">
                  {item.label}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export interface AgentCreateFormProps {
  className?: string;
  createError?: string | null;
  // The working team preselected in the team select.
  defaultTeamId?: string;
  isCreating?: boolean;
  isTeamsError?: boolean;
  isTeamsPending?: boolean;
  onCancel: () => void;
  onCreateAgent: (input: { displayName: string; teamId: string }) => void;
  teams: readonly TeamSelectorItem[];
}

// The ONE create-agent form (docs/09 §4: agents are added from the agent
// selector's create affordance or the /agents index page action — both render
// this). Field state is local and resets by unmount.
export function AgentCreateForm({
  className,
  createError,
  defaultTeamId,
  isCreating = false,
  isTeamsError = false,
  isTeamsPending = false,
  onCancel,
  onCreateAgent,
  teams,
}: AgentCreateFormProps) {
  const [displayName, setDisplayName] = useState("");
  const [teamId, setTeamId] = useState(defaultTeamId ?? "");
  const canCreate =
    displayName.trim().length > 0 && teamId.length > 0 && !isCreating;

  return (
    <form
      className={className}
      onSubmit={(event) => {
        event.preventDefault();
        if (!canCreate) {
          return;
        }
        onCreateAgent({
          displayName: displayName.trim(),
          teamId,
        });
      }}
    >
      <Stack gap={2}>
        <TextInput
          label="Name"
          value={displayName}
          onChange={(event) => {
            setDisplayName(event.currentTarget.value);
          }}
        />
        <Select
          label="Team"
          size="sm"
          value={teamId}
          onChange={(event) => {
            setTeamId(event.currentTarget.value);
          }}
        >
          <option value="">Select team</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.label}
            </option>
          ))}
        </Select>
        {isTeamsPending ? <Text variant="muted">Loading teams…</Text> : null}
        {isTeamsError ? <InlineError>Could not load teams.</InlineError> : null}
        {!isTeamsPending && !isTeamsError && teams.length === 0 ? (
          <Text variant="muted">Create a team in Settings first.</Text>
        ) : null}
        {createError ? <InlineError>{createError}</InlineError> : null}
        <Inline gap={2}>
          <Button disabled={!canCreate} size="sm" type="submit">
            {isCreating ? "Creating…" : "Create agent"}
          </Button>
          <Button
            size="sm"
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}

export interface AgentSelectorItem {
  createdAt?: string;
  id: string;
  label: string;
  // The agent's owning team — threaded through so selection can project the
  // team scope alongside the agent (agent implies team, docs/09 §4).
  teamId?: string;
}

export interface AgentSelectorProps {
  // The active agent, or undefined for the "All agents" (team-wide) scope.
  activeAgentId?: string;
  activeAgentLabel?: string;
  agents: readonly AgentSelectorItem[];
  createError?: string | null;
  // The working team preselected in the create form's team select.
  defaultTeamId?: string;
  hasNextPage?: boolean;
  isCreating?: boolean;
  isError?: boolean;
  isPending?: boolean;
  isOpen: boolean;
  isTeamsError?: boolean;
  isTeamsPending?: boolean;
  onCreateAgent: (input: { displayName: string; teamId: string }) => void;
  onCopyAgentId: (agentId: string) => void;
  onLoadMore?: () => void;
  onOpenAgent: (agentId: string, teamId?: string) => void;
  onOpenAgentVariables?: (agentId: string) => void;
  onOpenChange: (open: boolean) => void;
  // Archived agents are managed on the Team → Agents settings screen,
  // not in this menu; the feature layer owns the navigation.
  onOpenArchivedAgents?: () => void;
  onSelectAllAgents: () => void;
  teams: readonly TeamSelectorItem[];
}

// The sidebar agent selector (UI-LAW §8; moved from the top bar in shell v2):
// the working team's agents, an All-agents row (= team-wide), a paged list,
// and the inline create-agent affordance (team preselected from the working
// team). Data (the agent list, team list, create mutation) is owned by the
// feature layer and passed in.
export function AgentSelector(props: AgentSelectorProps) {
  return (
    <AgentSelectorContent key={props.isOpen ? "open" : "closed"} {...props} />
  );
}

function AgentSelectorContent({
  activeAgentId,
  activeAgentLabel,
  agents,
  createError,
  defaultTeamId,
  hasNextPage = false,
  isCreating = false,
  isError = false,
  isPending = false,
  isOpen,
  isTeamsError = false,
  isTeamsPending = false,
  onCreateAgent,
  onCopyAgentId,
  onLoadMore,
  onOpenAgent,
  onOpenAgentVariables,
  onOpenChange,
  onOpenArchivedAgents,
  onSelectAllAgents,
  teams,
}: AgentSelectorProps) {
  const [agentQuery, setAgentQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  // One row's menu at a time, so a single anchor/layer pair serves them all:
  // only the open row attaches the refs.
  const [openActionsAgentId, setOpenActionsAgentId] = useState<string | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const actionsAnchorRef = useRef<HTMLButtonElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const normalizedAgentQuery = agentQuery.trim().toLocaleLowerCase();
  const visibleAgents = useMemo(() => {
    const filteredAgents =
      normalizedAgentQuery.length > 0
        ? agents.filter((agent) =>
            agent.label.toLocaleLowerCase().includes(normalizedAgentQuery),
          )
        : agents;

    return [...filteredAgents].sort((first, second) => {
      // The scoped agent leads its own menu (matching the team selector), so
      // the current choice reads off the top instead of being hunted for in
      // the sorted list. Search still filters it out when it does not match.
      const activeDelta =
        Number(second.id === activeAgentId) -
        Number(first.id === activeAgentId);
      if (activeDelta !== 0) {
        return activeDelta;
      }
      return first.label.localeCompare(second.label, undefined, {
        sensitivity: "base",
      });
    });
  }, [activeAgentId, agents, normalizedAgentQuery]);

  const activeLabel = activeAgentId
    ? (activeAgentLabel ?? "Agent")
    : "All agents";

  // Drop the row menu too, so reopening never restores a stale one.
  const closeSelector = () => {
    setOpenActionsAgentId(null);
    onOpenChange(false);
  };

  useAnchoredLayerDismiss({
    active: isOpen,
    anchorRef: triggerRef,
    layerRef: menuRef,
    onDismiss: closeSelector,
  });

  // The row menu lives inside menuRef, so a click on the selector's own body
  // dismisses only the row menu; a click beyond the selector dismisses both,
  // as does Escape.
  useAnchoredLayerDismiss({
    active: openActionsAgentId !== null,
    anchorRef: actionsAnchorRef,
    layerRef: actionsMenuRef,
    onDismiss: () => {
      setOpenActionsAgentId(null);
    },
  });

  return (
    <div className="marker-agent-selector">
      <button
        aria-expanded={isOpen}
        aria-label="Select agent"
        className="marker-agent-selector__trigger"
        onClick={() => {
          onOpenChange(!isOpen);
        }}
        ref={triggerRef}
        title={activeLabel}
        type="button"
      >
        {/* Collapsed rail shows this agent icon in place of the label +
            chevron (CSS toggles visibility off the collapsed shell class). */}
        <span className="marker-agent-selector__icon">
          <ShellIcon name="bot" />
        </span>
        <span className="marker-agent-selector__label marker-fade-truncate">
          {activeLabel}
        </span>
        <ShellIcon name="chevronDown" />
      </button>
      {isOpen ? (
        <div className="marker-agent-selector__menu" ref={menuRef}>
          <div className="marker-agent-selector__search-row">
            <label className="marker-agent-selector__search">
              <ShellIcon name="search" />
              <input
                aria-label="Search agents"
                onChange={(event) => {
                  setAgentQuery(event.currentTarget.value);
                }}
                placeholder="Find agent"
                type="search"
                value={agentQuery}
              />
            </label>
            <button
              aria-label="New agent"
              className="marker-agent-selector__create-button"
              onClick={() => {
                setIsOverflowOpen(false);
                setOpenActionsAgentId(null);
                setIsCreateOpen((current) => !current);
              }}
              title="New agent"
              type="button"
            >
              <ShellIcon name="plus" />
            </button>
            {/* The overflow menu takes the old sort control's slot: agent
                order is now fixed (scoped agent first, then alphabetical),
                so a sort affordance bought nothing. Archived agents lives
                here rather than at the foot of the list. */}
            <div className="marker-agent-selector__overflow-anchor">
              <button
                aria-expanded={isOverflowOpen}
                aria-label="More agent actions"
                className="marker-agent-selector__overflow-button"
                onClick={() => {
                  setIsCreateOpen(false);
                  setOpenActionsAgentId(null);
                  setIsOverflowOpen((current) => !current);
                }}
                title="More"
                type="button"
              >
                <ShellIcon name="ellipsisVertical" />
              </button>
              {isOverflowOpen ? (
                <div className="marker-popover marker-agent-selector__overflow-menu">
                  <button
                    className="marker-menu-row"
                    onClick={() => {
                      setIsOverflowOpen(false);
                      onOpenChange(false);
                      onOpenArchivedAgents?.();
                    }}
                    type="button"
                  >
                    <ShellIcon name="trash" />
                    <span>Archived agents</span>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          {isCreateOpen ? (
            <AgentCreateForm
              className="marker-agent-selector__form"
              createError={createError}
              defaultTeamId={defaultTeamId}
              isCreating={isCreating}
              isTeamsError={isTeamsError}
              isTeamsPending={isTeamsPending}
              teams={teams}
              onCancel={() => {
                setIsCreateOpen(false);
              }}
              onCreateAgent={onCreateAgent}
            />
          ) : null}
          <button
            aria-current={activeAgentId ? undefined : "true"}
            className="marker-menu-row marker-agent-selector__all"
            onClick={() => {
              onOpenChange(false);
              onSelectAllAgents();
            }}
            type="button"
          >
            All agents
          </button>
          {isPending ? (
            <div className="marker-agent-selector__note">Loading agents…</div>
          ) : null}
          {isError ? (
            <div className="marker-agent-selector__note">
              Could not load agents.
            </div>
          ) : null}
          {visibleAgents.map((agent) => {
            const isActive = agent.id === activeAgentId;
            const isActionsOpen = openActionsAgentId === agent.id;
            return (
              <div
                className={classes(
                  [
                    "marker-agent-selector__agent",
                    isActionsOpen
                      ? "marker-agent-selector__agent--actions-open"
                      : "",
                  ],
                  undefined,
                )}
                key={agent.id}
              >
                <button
                  aria-current={isActive ? "true" : undefined}
                  className={classes(
                    [
                      "marker-menu-row",
                      "marker-agent-selector__agent-row",
                      isActive ? "marker-menu-row--active" : "",
                    ],
                    undefined,
                  )}
                  onClick={() => {
                    closeSelector();
                    onOpenAgent(agent.id, agent.teamId);
                  }}
                  title={agent.label}
                  type="button"
                >
                  <span className="marker-fade-truncate">{agent.label}</span>
                </button>
                {/* UI-LAW §2.12: a kebab-triggered Menu, never a panel the
                    row's own hover opens — that cannot be reached across the
                    trigger-to-panel gap. */}
                <button
                  aria-expanded={isActionsOpen}
                  aria-haspopup="menu"
                  aria-label={`${agent.label} actions`}
                  className="marker-agent-selector__agent-kebab"
                  onClick={() => {
                    setIsCreateOpen(false);
                    setIsOverflowOpen(false);
                    setOpenActionsAgentId(isActionsOpen ? null : agent.id);
                  }}
                  ref={isActionsOpen ? actionsAnchorRef : undefined}
                  type="button"
                >
                  <ShellIcon name="ellipsis" />
                </button>
                {isActionsOpen ? (
                  <div
                    aria-label={`${agent.label} actions`}
                    className="marker-popover marker-agent-selector__actions"
                    ref={actionsMenuRef}
                    role="menu"
                  >
                    <button
                      className="marker-menu-row"
                      onClick={() => {
                        closeSelector();
                        onOpenAgent(agent.id, agent.teamId);
                      }}
                      role="menuitem"
                      type="button"
                    >
                      <ShellIcon name="pencil" />
                      <span>Edit agent</span>
                    </button>
                    {onOpenAgentVariables ? (
                      <button
                        className="marker-menu-row"
                        onClick={() => {
                          closeSelector();
                          onOpenAgentVariables(agent.id);
                        }}
                        role="menuitem"
                        type="button"
                      >
                        <ShellIcon name="settings" />
                        <span>Variables</span>
                      </button>
                    ) : null}
                    <button
                      className="marker-menu-row"
                      onClick={() => {
                        closeSelector();
                        onCopyAgentId(agent.id);
                      }}
                      role="menuitem"
                      type="button"
                    >
                      <ShellIcon name="copy" />
                      <span>Copy agent ID</span>
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
          {normalizedAgentQuery.length > 0 &&
          !isPending &&
          !isError &&
          visibleAgents.length === 0 ? (
            <div className="marker-agent-selector__note">
              No matching agents.
            </div>
          ) : null}
          {hasNextPage && onLoadMore ? (
            <button
              className="marker-menu-row"
              onClick={onLoadMore}
              type="button"
            >
              <ShellIcon name="chevronDown" />
              <span>Load more agents</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export type ThemePreference = "dark" | "light" | "system";

export interface ThemeControlProps {
  onSelect: (preference: ThemePreference) => void;
  value: ThemePreference;
}

const THEME_CONTROL_OPTIONS: ReadonlyArray<{
  icon: ShellIconName;
  label: string;
  value: ThemePreference;
}> = [
  { icon: "sun", label: "Light", value: "light" },
  { icon: "moon", label: "Dark", value: "dark" },
  { icon: "monitor", label: "System", value: "system" },
];

// The light / dark / system segmented control lives inside the avatar menu;
// there is no standalone top-bar theme button. Icon-only options over a thumb
// that slides to the active option (`data-active-index` drives its transform).
export function ThemeControl({ onSelect, value }: ThemeControlProps) {
  const activeIndex = THEME_CONTROL_OPTIONS.findIndex(
    (option) => option.value === value,
  );
  return (
    <div aria-label="Theme" className="marker-theme-control" role="group">
      <div
        className="marker-theme-control__track"
        data-active-index={Math.max(activeIndex, 0)}
      >
        <span aria-hidden="true" className="marker-theme-control__thumb" />
        {THEME_CONTROL_OPTIONS.map((option) => (
          <button
            aria-label={option.label}
            aria-pressed={option.value === value}
            className={classes(
              [
                "marker-theme-control__option",
                option.value === value
                  ? "marker-theme-control__option--active"
                  : "",
              ],
              undefined,
            )}
            key={option.value}
            onClick={() => {
              onSelect(option.value);
            }}
            title={option.label}
            type="button"
          >
            <ShellIcon name={option.icon} />
          </button>
        ))}
      </div>
    </div>
  );
}

export interface TopBarProps {
  accountEmail?: string;
  accountInitials: string;
  accountLabel: string;
  breadcrumbs: ShellBreadcrumb[];
  contextActions?: readonly TopBarContextAction[];
  contextLabel?: string;
  contextMenuLabel?: string;
  docsHref?: string;
  onOpenContext?: () => void;
  onOpenProfile?: () => void;
  onOpenSearch: () => void;
  onSelectThemePreference: (preference: ThemePreference) => void;
  supportHref?: string;
  themePreference: ThemePreference;
}

export interface TopBarContextAction {
  icon: ShellIconName;
  label: string;
  onSelect: () => void;
}

// Top bar anatomy (UI-LAW §8): the active agent scope is the breadcrumb base,
// then route breadcrumbs, then the right cluster — search (⌘K), docs, and the
// avatar menu (name + email header, ThemeControl, Profile, support, Sign out).
export function TopBar({
  accountEmail,
  accountInitials,
  accountLabel,
  breadcrumbs,
  contextActions = [],
  contextLabel,
  contextMenuLabel,
  docsHref,
  onOpenContext,
  onOpenProfile,
  onOpenSearch,
  onSelectThemePreference,
  supportHref,
  themePreference,
}: TopBarProps) {
  const [openMenu, setOpenMenu] = useState<"context" | "account" | null>(null);
  const contextTriggerRef = useRef<HTMLButtonElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const accountTriggerRef = useRef<HTMLButtonElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const hasContextActions = contextActions.length > 0;

  useAnchoredLayerDismiss({
    active: openMenu === "context",
    anchorRef: contextTriggerRef,
    layerRef: contextMenuRef,
    onDismiss: () => {
      setOpenMenu(null);
    },
  });

  useAnchoredLayerDismiss({
    active: openMenu === "account",
    anchorRef: accountTriggerRef,
    layerRef: accountMenuRef,
    onDismiss: () => {
      setOpenMenu(null);
    },
  });

  return (
    <header className="marker-top-bar">
      <div className="marker-top-bar__breadcrumbs">
        {contextLabel ? (
          <>
            <div className="marker-top-bar__context">
              {onOpenContext ? (
                <button
                  className="marker-top-bar__context-label"
                  onClick={onOpenContext}
                  title={contextLabel}
                  type="button"
                >
                  {contextLabel}
                </button>
              ) : (
                <span
                  className="marker-top-bar__context-label"
                  title={contextLabel}
                >
                  {contextLabel}
                </span>
              )}
              {hasContextActions ? (
                <div className="marker-top-bar__menu-anchor">
                  <button
                    aria-expanded={openMenu === "context"}
                    aria-label={contextMenuLabel ?? `${contextLabel} actions`}
                    className="marker-top-bar__context-menu-button"
                    onClick={() => {
                      setOpenMenu((current) =>
                        current === "context" ? null : "context",
                      );
                    }}
                    ref={contextTriggerRef}
                    title={contextMenuLabel ?? `${contextLabel} actions`}
                    type="button"
                  >
                    <ShellIcon name="ellipsis" />
                  </button>
                  {openMenu === "context" ? (
                    <div
                      className="marker-top-bar__menu marker-top-bar__context-menu"
                      ref={contextMenuRef}
                    >
                      {contextActions.map((item) => (
                        <button
                          className="marker-menu-row"
                          key={item.label}
                          onClick={() => {
                            setOpenMenu(null);
                            item.onSelect();
                          }}
                          type="button"
                        >
                          <ShellIcon name={item.icon} />
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            {breadcrumbs.length > 0 ? (
              <span
                aria-hidden="true"
                className="marker-top-bar__breadcrumb-separator"
              >
                /
              </span>
            ) : null}
          </>
        ) : null}
        <Breadcrumbs items={breadcrumbs} />
      </div>
      <div className="marker-top-bar__actions">
        <button
          aria-label="Search"
          className="marker-top-bar__search"
          onClick={onOpenSearch}
          title="Search (Command-K)"
          type="button"
        >
          <ShellIcon name="search" />
          <span>Search</span>
          <kbd>⌘K</kbd>
        </button>
        {docsHref ? (
          <a
            aria-label="Documentation"
            className="marker-top-bar__button"
            href={docsHref}
            rel="noopener"
            target="_blank"
            title="Documentation"
          >
            <ShellIcon name="book" />
          </a>
        ) : null}
        <div className="marker-top-bar__menu-anchor">
          <button
            aria-expanded={openMenu === "account"}
            aria-label={accountLabel}
            className="marker-top-bar__avatar"
            onClick={() => {
              setOpenMenu((current) =>
                current === "account" ? null : "account",
              );
            }}
            ref={accountTriggerRef}
            title={accountLabel}
            type="button"
          >
            {accountInitials}
          </button>
          {openMenu === "account" ? (
            <div
              className="marker-top-bar__menu marker-top-bar__menu--right"
              ref={accountMenuRef}
            >
              <div className="marker-menu-account">
                <span className="marker-menu-account__name">
                  {accountLabel}
                </span>
                {accountEmail ? (
                  <span className="marker-menu-account__email">
                    {accountEmail}
                  </span>
                ) : null}
              </div>
              <ThemeControl
                onSelect={onSelectThemePreference}
                value={themePreference}
              />
              {onOpenProfile ? (
                <button
                  className="marker-menu-row"
                  onClick={() => {
                    setOpenMenu(null);
                    onOpenProfile();
                  }}
                  type="button"
                >
                  <ShellIcon name="user" />
                  <span>Profile</span>
                </button>
              ) : null}
              {supportHref ? (
                <a className="marker-menu-row" href={supportHref}>
                  <ShellIcon name="mail" />
                  <span>Support</span>
                </a>
              ) : null}
              <form action="/auth/logout" method="post">
                <button
                  className="marker-menu-row marker-menu-row--danger"
                  type="submit"
                >
                  <ShellIcon name="logOut" />
                  <span>Sign out</span>
                </button>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

export interface ShellIconProps {
  name: ShellIconName;
}

export function ShellIcon({ name }: ShellIconProps) {
  return (
    <svg
      aria-hidden="true"
      className="marker-shell-icon"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      {iconPath(name)}
    </svg>
  );
}

function iconPath(name: ShellIconName) {
  switch (name) {
    case "alerts":
      return (
        <>
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </>
      );
    case "apiKeys":
      return (
        <>
          <path d="m15.5 7.5 1 1" />
          <path d="m11 12 4.5-4.5" />
          <path d="M7 14a4 4 0 1 1 2.83 1.17L7 18H4v-3z" />
        </>
      );
    case "arrowLeft":
      return <path d="m15 18-6-6 6-6" />;
    case "arrowUpRight":
      return (
        <>
          <path d="M7 17 17 7" />
          <path d="M7 7h10v10" />
        </>
      );
    case "bot":
      return (
        <>
          <path d="M12 8V4H8" />
          <rect height="12" rx="2" width="16" x="4" y="8" />
          <path d="M2 14h2" />
          <path d="M20 14h2" />
          <path d="M15 13v2" />
          <path d="M9 13v2" />
        </>
      );
    case "book":
      return (
        <>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" />
        </>
      );
    case "building":
      return (
        <>
          <rect height="18" rx="1" width="14" x="5" y="3" />
          <path d="M9 7h1" />
          <path d="M14 7h1" />
          <path d="M9 11h1" />
          <path d="M14 11h1" />
          <path d="M9 15h1" />
          <path d="M14 15h1" />
        </>
      );
    case "check":
      return <path d="m5 12 5 5L20 7" />;
    case "chevronDown":
      return <path d="m6 9 6 6 6-6" />;
    case "clipboardCheck":
      return (
        <>
          <rect height="4" rx="1" width="8" x="8" y="2" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <path d="m9 14 2 2 4-4" />
        </>
      );
    case "copy":
      return (
        <>
          <rect height="14" rx="2" width="14" x="8" y="8" />
          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
        </>
      );
    case "ellipsis":
      return (
        <>
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </>
      );
    case "ellipsisVertical":
      return (
        <>
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="19" r="1" />
        </>
      );
    case "pencil":
      return (
        <>
          <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
          <path d="m15 5 4 4" />
        </>
      );
    case "play":
      return <path d="m6 3 14 9-14 9V3z" />;
    case "trash":
      return (
        <>
          <path d="M3 6h18" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </>
      );
    case "dashboard":
      return (
        <>
          <rect height="9" rx="1" width="7" x="3" y="3" />
          <rect height="5" rx="1" width="7" x="14" y="3" />
          <rect height="9" rx="1" width="7" x="14" y="12" />
          <rect height="5" rx="1" width="7" x="3" y="16" />
        </>
      );
    case "externalLink":
      return (
        <>
          <path d="M15 3h6v6" />
          <path d="M10 14 21 3" />
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        </>
      );
    case "fileText":
      return (
        <>
          <path d="M14 2v4a2 2 0 0 0 2 2h4" />
          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
          <path d="M8 13h8" />
          <path d="M8 17h5" />
        </>
      );
    case "flask":
      return (
        <>
          <path d="M10 2v7.31" />
          <path d="M14 9.3V1.99" />
          <path d="M8.5 2h7" />
          <path d="M14 9.3a6.5 6.5 0 1 1-4 0" />
          <path d="M5.52 16h12.96" />
        </>
      );
    case "help":
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <path d="M12 17h.01" />
        </>
      );
    case "listFilter":
      return (
        <>
          <path d="M3 6h18" />
          <path d="M7 12h10" />
          <path d="M10 18h4" />
        </>
      );
    case "logOut":
      return (
        <>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="m16 17 5-5-5-5" />
          <path d="M21 12H9" />
        </>
      );
    case "mail":
      return (
        <>
          <rect height="16" rx="2" width="20" x="2" y="4" />
          <path d="m22 7-10 6L2 7" />
        </>
      );
    case "monitor":
      return (
        <>
          <rect height="14" rx="2" width="20" x="2" y="3" />
          <path d="M8 21h8" />
          <path d="M12 17v4" />
        </>
      );
    case "moon":
      return <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z" />;
    case "panel":
      return (
        <>
          <rect height="18" rx="2" width="18" x="3" y="3" />
          <path d="M9 3v18" />
        </>
      );
    case "plus":
      return (
        <>
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </>
      );
    // The scripted path a simulated caller takes — the Scenarios rail item.
    // Deliberately not a paper/document silhouette: `fileText` (Transcripts),
    // `clipboardCheck` (Markers) and the top bar's `book` (Documentation)
    // already own those, and the rail goes icon-only when collapsed.
    case "route":
      return (
        <>
          <circle cx="6" cy="19" r="3" />
          <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
          <circle cx="18" cy="5" r="3" />
        </>
      );
    case "search":
      return (
        <>
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </>
      );
    case "settings":
      return (
        <>
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
          <circle cx="12" cy="12" r="3" />
        </>
      );
    case "sun":
      return (
        <>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.41 1.41" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.41" />
        </>
      );
    case "trend":
      return (
        <>
          <path d="M3 3v16a2 2 0 0 0 2 2h16" />
          <path d="M18 17V9" />
          <path d="M13 17V5" />
          <path d="M8 17v-3" />
        </>
      );
    case "user":
      return (
        <>
          <circle cx="12" cy="8" r="4" />
          <path d="M20 21a8 8 0 0 0-16 0" />
        </>
      );
    case "users":
      return (
        <>
          <circle cx="9" cy="8" r="4" />
          <path d="M15 21a6 6 0 0 0-12 0" />
          <path d="M16 4a4 4 0 0 1 0 8" />
          <path d="M17 15a6 6 0 0 1 4 6" />
        </>
      );
  }
}
